import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { Response } from '../models/core/response.model';
import { PaginatedResponse } from '../models/core/paginated-response.model';
import { Event } from '../models/core/event.model';
import { Observable } from 'rxjs';
import { EventDetail } from '../models/core/event-detail.model';
import { EventAssistant } from '../models/core/event-assistant.model';
import { ScannerResponseQR } from '../models/core/scanner-response-qr.model';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  private userAcessToken: string | undefined;

  constructor(
    private _http: HttpClient,
    private _auth: AuthService
  ) {
    this.loadUserAccessToken();
  }

  /**
   * Load user access token
   */
  public loadUserAccessToken() {
    this._auth.getLogin().then((res) => {
      if (res) {
        this.userAcessToken = res.access_token;
      }
    });
  }

  /**
   * Request Headers
   */
  private get requestHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    headers = headers.set("Authorization", `Bearer ${this.userAcessToken}`);

    return headers;
  }

  /**
   * Request Options
   */
  private get requestOptions() {
    const headers = this.requestHeaders;

    return {
      headers: headers
    };
  }

  /**
   * Get Events
   */
  public async getEvents(page?: number, perPage?: number): Promise<Observable<PaginatedResponse<Event>>> {
    const url = `${environment.api_server}/api/v2/scanner/events`;
    const headers = await this.getAuthHeaders();

    let params: any = {};
    if (page) params.page = page;
    if (perPage) params.per_page = perPage;

    return this._http.get<PaginatedResponse<Event>>(url, {
      headers,
      params
    });
  }

  /**
   * Get Past Events
   */
  public async getEventsPast(page?: number, perPage?: number): Promise<Observable<PaginatedResponse<Event>>> {
    const url = `${environment.api_server}/api/v2/scanner/events/past`;
    const headers = await this.getAuthHeaders();

    let params: any = {};
    if (page) params.page = page;
    if (perPage) params.per_page = perPage;

    return this._http.get<PaginatedResponse<Event>>(url, {
      headers,
      params
    });
  }

  /**
   * Get Event
   */
  public async getEvent(id: string): Promise<Observable<Response<EventDetail>>> {
    const url = `${environment.api_server}/api/v2/scanner/events/event/${id}`;
    const params = { with_permissions: true };
    const headers = await this.getAuthHeaders();

    return this._http.get<Response<EventDetail>>(url, { params, headers });
  }

  /**
   * Scan QR Code
   * @param event_id Event ID
   * @param payload QR code payload
   * @param auto_verify If true, automatically verify the assistant if valid (default: false)
   */
  public async scanQRCode(event_id: string, payload: string | null = null, auto_verify: boolean = false): Promise<Observable<ScannerResponseQR>> {
    let url = `${environment.api_server}/api/v2/scanner/${payload}/${event_id}`;
    
    if (auto_verify) {
      url += '?auto_verify=true';
    }
    
    const headers = await this.getAuthHeaders();

    return this._http.get<ScannerResponseQR>(url, { headers });
  }

  /**
   * Search Event Assistant
   */
  public searchEventAssistant(event_id: string, query: string | null = null) {
    const url = `${environment.api_server}/api/v2/scanner/search-assistant`;

    const params: any = {
      event_id: event_id
    }

    if (query) {
      params.q = query;
    }

    return this._http.get<Response<Array<EventAssistant>>>(url, {
      params: params,
      headers: this.requestHeaders
    });
  }

  /**
   * Scan QR Code
   */
  public verifyAssistant(event_id: string, assistant_uuid: string, product_id: string, manual_verified: boolean) {
    const url = `${environment.api_server}/api/v2/scanner/verify`;

    const body = {
      assistant_uuid: assistant_uuid,
      manual_verified: manual_verified,
      product_id: product_id,
      event_id: event_id,
    }

    return this._http.post<ScannerResponseQR>(url, body, this.requestOptions);
  }

  /**
   * Get current user info (/me)
   */
  public async getMyData(): Promise<Observable<any>> {
    const url = `${environment.api_server}/api/me`;
    const headers = await this.getAuthHeaders();

    return this._http.get<any>(url, { headers });
  }

  public async getAuthHeaders(): Promise<HttpHeaders> {
    if (!this.userAcessToken) {
      const login = await this._auth.getLogin();
      if (!login?.access_token) throw new Error('Missing access token');
      this.userAcessToken = login.access_token;
    }

    return new HttpHeaders().set('Authorization', `Bearer ${this.userAcessToken}`);
  }

  /**
   * Get Events with Turnstiles
   */
  public async getEventsWithTurnstiles(
    dateFilter?: 'upcoming' | 'past',
    page?: number,
    perPage?: number
  ): Promise<Observable<any>> {
    const url = `${environment.api_server}/api/user/events-with-turnstiles`;
    const headers = await this.getAuthHeaders();

    let params: any = {};
    if (dateFilter) params.date_filter = dateFilter;
    if (page) params.page = page;
    if (perPage) params.per_page = perPage;

    return this._http.get<any>(url, { headers, params });
  }

  /**
   * Get Turnstiles for a specific event
   */
  public async getTurnstilesForEvent(eventId: number): Promise<Observable<any>> {
    const url = `${environment.api_server}/api/events/${eventId}/turnstiles`;
    const headers = await this.getAuthHeaders();

    return this._http.get<any>(url, { headers });
  }

  /**
   * Get Recent Scans for a specific event
   */
  public async getRecentScans(
    eventId: number,
    limit: number = 10
  ): Promise<Observable<any>> {
    const url = `${environment.api_server}/api/events/${eventId}/turnstiles/recent-scans`;
    const headers = await this.getAuthHeaders();

    const params: any = {
      limit: limit.toString()
    };

    return this._http.get<any>(url, { headers, params });
  }

  public async registerManualEntry(
    turnstileId: number,
    assistantUuid: string,
    eventId?: number
  ): Promise<Observable<any>> {
    const url = `${environment.api_server}/api/turnstiles/${turnstileId}/manual-entry`;
    const headers = await this.getAuthHeaders();

    const body: any = {
      assistant_uuid: assistantUuid,
      turnstile_id: turnstileId
    };

    if (eventId) {
      body.event_id = eventId;
    }

    return this._http.post<any>(url, body, { headers });
  }

  /**
   * Get Event Turnstiles List
   */
  public async getEventTurnstilesList(eventId: number): Promise<Observable<any>> {
    const url = `${environment.api_server}/api/events/${eventId}/turnstiles/list`;
    const headers = await this.getAuthHeaders();

    return this._http.get<any>(url, { headers });
  }

  /**
   * Get Event Turnstiles History
   */
  public async getEventTurnstilesHistory(
    eventId: number,
    params?: {
      turnstile_id?: number;
      filter_status?: string;
      assistant_search?: string;
      limit?: number;
      page?: number;
    }
  ): Promise<Observable<any>> {
    const url = `${environment.api_server}/api/events/${eventId}/turnstiles/history`;
    const headers = await this.getAuthHeaders();

    let queryParams: any = {};
    if (params?.turnstile_id) queryParams.turnstile_id = params.turnstile_id;
    if (params?.filter_status) queryParams.filter_status = params.filter_status;
    if (params?.assistant_search) queryParams.assistant_search = params.assistant_search;
    if (params?.limit) queryParams.limit = params.limit;
    if (params?.page) queryParams.page = params.page;

    return this._http.get<any>(url, { headers, params: queryParams });
  }
}